import React from "react";
import { T } from "./theme";
import tapeBadge from "./assets/tape-badge.png";

export const GENRES = ["Rock", "Soul", "Country", "Jazz", "Pop", "Blues", "Funk", "Country/Rock", "Other"];

export const COUNTRIES = [
  "Unknown", "United States", "Canada", "United Kingdom", "Japan", "Ireland",
  "Australia", "New Zealand", "Germany", "Italy", "France", "Spain", "Sweden",
  "Netherlands", "Mexico", "Brazil", "Philippines", "South Korea", "Other",
];

export const CONDITIONS = [
  "Sealed", "Mint", "Near Mint", "Very Good Plus", "Very Good",
  "Good", "Fair", "Poor", "Parts / Repair", "Unknown",
];

const CONDITION_COLORS = {
  Sealed: "#4ade80", Mint: "#4ade80", "Near Mint": "#86efac",
  "Very Good Plus": "#a3e635", "Very Good": "#d4d420",
  Good: "#facc15", Fair: "#fb923c", Poor: "#f87171",
  "Parts / Repair": "#ef4444", Unknown: "#ccc",
};

export const cColor = (c) => CONDITION_COLORS[c] || "#ccc";
export const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// The badge art isn't square (486x600) — fix height and let width follow its
// real aspect ratio so it's never stretched or squashed into a square box.
export const TapeIcon = ({ size = 36 }) => (
  <img src={tapeBadge} alt="" style={{ height: size * 1.5, width: "auto", display: "block" }} />
);

// Real vector icons for the bottom nav — unlike emoji, these render at an exact,
// identical size on every device and properly inherit color (emoji ignore CSS
// `color` entirely, so the active-tab highlight silently did nothing for them).
const NAV_ICON_PATHS = {
  home: <path d="M4 11.5 12 4.5l8 7M6.5 9.8V19h4.2v-5h2.6v5H17.5V9.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />,
  collection: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><rect x="4" y="4" width="7" height="7" rx="1.2" /><rect x="13" y="4" width="7" height="7" rx="1.2" /><rect x="4" y="13" width="7" height="7" rx="1.2" /><rect x="13" y="13" width="7" height="7" rx="1.2" /></g>,
  add: <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />,
  shop: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8h12l-1 12H7L6 8z" /><path d="M9 8V6.5a3 3 0 0 1 6 0V8" /></g>,
  wishlist: <path d="M12 3.5 14.5 9l6 .6-4.5 4 1.3 5.9L12 16.5l-5.3 3 1.3-5.9-4.5-4 6-.6L12 3.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />,
  profile: <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="9.3" r="3.1" /><path d="M6 19a6.2 6.2 0 0 1 12 0" /></g>,
};

export const NavIcon = ({ name, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">{NAV_ICON_PATHS[name]}</svg>
);

export const Btn = ({ children, onClick, variant = "primary", disabled, style = {} }) => (
  <button disabled={disabled} onClick={onClick} style={{
    background: variant === "primary" ? T.btnBg : variant === "gold" ? T.btnGold : variant === "danger" ? "#C0392B" : "transparent",
    border: variant === "outline" ? `1px solid ${T.border}` : "none",
    color: variant === "outline" ? T.muted : "#fff",
    padding: "10px 18px", borderRadius: 8, fontSize: 12, fontWeight: "bold",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, fontFamily: T.font, letterSpacing: "0.08em",
    textTransform: "uppercase", ...style
  }}>{children}</button>
);

export const Badge = ({ condition }) => (
  <span style={{
    background: cColor(condition) + "22", border: `1px solid ${cColor(condition)}55`,
    color: cColor(condition), fontSize: 9, padding: "2px 6px", borderRadius: 4,
    letterSpacing: "0.08em", fontWeight: "bold"
  }}>{condition}</span>
);

export const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
    <div style={{ flex: 1, height: 1, background: T.border }} />
    {children}
    <div style={{ flex: 1, height: 1, background: T.border }} />
  </div>
);

export const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 10, boxShadow: "0 1px 4px rgba(59,42,26,0.06)", ...style }}>{children}</div>
);

export const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 10 }}>
    {label && <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 3 }}>{label}</label>}
    <input style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "9px 11px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box" }} {...props} />
  </div>
);

export const StarRating = ({ value = 0, onChange, size = 20, readOnly = false }) => (
  <div style={{ display: "inline-flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        key={n}
        onClick={readOnly ? undefined : () => onChange(n === value ? 0 : n)}
        style={{
          fontSize: size,
          color: n <= value ? T.accentAlt : T.border,
          cursor: readOnly ? "default" : "pointer",
          lineHeight: 1,
        }}
        role={readOnly ? undefined : "button"}
        aria-label={readOnly ? undefined : `Rate ${n} star${n > 1 ? "s" : ""}`}
      >
        ★
      </span>
    ))}
  </div>
);

export const PhotoPicker = ({ items, onChange, max = 5, label = "Photos" }) => {
  const addPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || items.length >= max) return;
    onChange([...items, { url: URL.createObjectURL(file), file }]);
  };
  const removePhoto = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>{label} ({items.length}/{max})</label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {items.map((item, i) => (
          <div key={i} style={{ position: "relative", width: 64, height: 64 }}>
            <img src={item.url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${T.border}` }} />
            <button onClick={() => removePhoto(i)} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 10, background: "#C0392B", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {items.length < max && (
          <label style={{ width: 64, height: 64, borderRadius: 8, border: `1px dashed ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.muted, fontSize: 22 }}>
            +
            <input type="file" accept="image/*" onChange={addPhoto} style={{ display: "none" }} />
          </label>
        )}
      </div>
    </div>
  );
};

export const Select = ({ label, options, ...props }) => (
  <div style={{ marginBottom: 10 }}>
    {label && <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 3 }}>{label}</label>}
    <select style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "9px 11px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box" }} {...props}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
